// filepath: /src/components/Formularios/PlanFormModal.js
import React, { useState } from 'react';
import './PlanFormModal.css';
import QRScanner from './QRScanner';
import Select2 from '../Select2';
import PrivacyNoticeModal from '../PrivacyNoticeModal';
import { useRegistration } from '../../hooks/useRegistration';
import formacionAcademica from '../../data/formacionAcademica.json';

const PlanFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  selectedPlan, 
  formData, 
  handleInputChange,
  stepInfo = { current: 1, total: 3, stepName: "Datos Personales" }
}) => {
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const { entidades, isLoading } = useRegistration();
  
  // Transformar entidades para Select2
  const entidadesOptions = entidades.map(entidad => ({
    value: entidad.id,
    label: `${String(entidad.id).padStart(2, '0')} - ${entidad.name?.toUpperCase() || entidad.vc_nombre?.toUpperCase()}`
  }));
  
  // Transformar formación académica para Select2
  const formacionAcademicaOptions = formacionAcademica.map(formacion => ({
    value: String(formacion.id),
    label: `${formacion.vc_nombre?.trim()} - ${formacion.vc_grupo?.trim()}`,
    grado: formacion.nu_grado,
    grupo: formacion.vc_grupo?.trim()
  }));

  // Manejar cambio en Select2 de entidad (estado de nacimiento)
  const handleEntidadNacimientoChange = (selectedValue) => {
    const syntheticEvent = {
      target: {
        name: 'birthState',
        value: selectedValue || '',
        type: 'select'
      }
    };
    handleInputChange(syntheticEvent);
  };

  // Manejar cambio en Select2 de formación académica
  const handleFormacionAcademicaChange = (selectedValue) => {
    const syntheticEvent = {
      target: {
        name: 'academicFormation',
        value: selectedValue || '',
        type: 'select'
      }
    };
    handleInputChange(syntheticEvent);
  };

  // Obtener el valor actual para el Select2 de entidad de nacimiento
  const getCurrentEntidadNacimiento = () => {
    const currentValue = formData.birthState;
    if (!currentValue) return null;
    
    const searchValue = String(currentValue);
    return entidadesOptions.find(option => String(option.value) === searchValue) || null;
  };

  // Obtener el valor actual para el Select2 de formación académica
  const getCurrentFormacionAcademica = () => {
    const currentValue = formData.academicFormation;
    if (!currentValue) return null;
    
    // Buscar la opción que coincida con el valor actual
    const found = formacionAcademicaOptions.find(option => 
      String(option.value) === String(currentValue)
    );
    
    return found || null;
  };
  
  const handleQRScanResult = (qrData) => {
    // Actualizar los campos del formulario con los datos del QR
    Object.keys(qrData).forEach(key => {
      if (qrData[key]) {
        const syntheticEvent = {
          target: {
            name: key,
            value: qrData[key],
            type: 'text'
          }
        };
        handleInputChange(syntheticEvent);
      }
    });
    setIsQRScannerOpen(false);
  };

  const openQRScanner = () => {
    setIsQRScannerOpen(true);
  };
  
  if (!isOpen) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content plan-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title">
            <h2>
              {selectedPlan?.monthlyPrice === 0 
                ? `Solicitar Licencia ${selectedPlan?.name}` 
                : `Configurar Plan ${selectedPlan?.name}`
              }
            </h2>
            <div className="step-indicator">
              <span className="step-text">Paso {stepInfo.current} de {stepInfo.total}</span>
              <span className="step-name">{stepInfo.stepName}</span>
            </div>
          </div>
          <div className="header-actions">
            <button className="qr-scan-button" onClick={openQRScanner} title="Escanear QR para llenar datos">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="5" height="5" stroke="currentColor" strokeWidth="2"/>
                <rect x="16" y="3" width="5" height="5" stroke="currentColor" strokeWidth="2"/>
                <rect x="3" y="16" width="5" height="5" stroke="currentColor" strokeWidth="2"/>
                <path d="M9 9h1v1h-1zM14 9h1v1h-1zM9 14h1v1h-1z" fill="currentColor"/>
                <path d="M16 14h2v2h-2zM16 18h2v2h-2zM18 16h2v2h-2z" fill="currentColor"/>
              </svg>
              QR
            </button>
            <button className="modal-close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="modal-body">
          <div className="plan-summary">
            <div className="plan-info">
              <h3>{selectedPlan?.name}</h3>
              <p>{selectedPlan?.description}</p>
              <div className="plan-price">
                <span className="price-amount">
                  {formatPrice(selectedPlan?.monthlyPrice || 0)}
                </span>
                <span className="price-period">/mes</span>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="plan-form">
            <div className="form-section">
              <h4>Información Personal</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="curp">CURP *</label>
                  <input
                    type="text"
                    id="curp"
                    name="curp"
                    value={formData.curp}
                    onChange={handleInputChange}
                    required
                    placeholder="CURP"
                    maxLength="18"
                    pattern="[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9]{2}"
                    title="Formato: AAAA000000HAAAAA00"
                  />
                  {formData.curp && !/^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9]{2}$/.test(formData.curp) && (
                    <span className="error-message">*Formato inválido</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="name">Nombre *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Nombre"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="paternalLastName">Apellido Paterno *</label>
                  <input
                    type="text"
                    id="paternalLastName"
                    name="paternalLastName"
                    value={formData.paternalLastName}
                    onChange={handleInputChange}
                    required
                    placeholder="Apellido Paterno"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="maternalLastName">Apellido Materno</label>
                  <input
                    type="text"
                    id="maternalLastName"
                    name="maternalLastName"
                    value={formData.maternalLastName}
                    onChange={handleInputChange}
                    placeholder="Apellido Materno"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Sexo *</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sexo *</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="birthDate">Fecha de Nacimiento *</label>
                  <div className="custom-date-wrapper">
                    <input
                      type="date"
                      id="birthDate"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      required
                      placeholder="dd-mm-yyyy"
                    />
                    <button
                      type="button"
                      className="custom-date-btn"
                      tabIndex={-1}
                      aria-label="Abrir calendario"
                      onClick={() => document.getElementById('birthDate')?.showPicker ? document.getElementById('birthDate').showPicker() : document.getElementById('birthDate')?.focus()}
                    >
                      <svg viewBox="0 0 24 24">
                        <rect x="3" y="5" width="18" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2"/>
                        <path d="M16 3v4M8 3v4M3 9h18" stroke="currentColor" strokeWidth="2"/>
                        <rect x="7" y="13" width="2" height="2" rx="1" fill="currentColor"/>
                        <rect x="11" y="13" width="2" height="2" rx="1" fill="currentColor"/>
                        <rect x="15" y="13" width="2" height="2" rx="1" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="birthState">Entidad de Nacimiento *</label>
                  <Select2
                    name="birthState"
                    value={getCurrentEntidadNacimiento()}
                    onChange={handleEntidadNacimientoChange}
                    options={entidadesOptions}
                    placeholder="Selecciona tu entidad de nacimiento"
                    isLoading={isLoading}
                    isSearchable={true}
                    isClearable={false}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Información de Perfil</h4>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="academicFormation">Formación Académica *</label>
                  <Select2
                    name="academicFormation"
                    value={getCurrentFormacionAcademica()}
                    onChange={handleFormacionAcademicaChange}
                    options={formacionAcademicaOptions}
                    placeholder="Busca tu formación académica (ej: medicina, enfermeria, etc.)"
                    isSearchable={true}
                    isClearable={false}
                    className="form-select2"
                    menuHeight={400}
                    menuPlacement="auto"
                    key={`formacion-${formData.academicFormation || 'empty'}`} // Force re-render when value changes
                    filterOption={(option, inputValue) => {
                      if (!inputValue || inputValue.length < 2) return false;
                      const searchValue = inputValue.toLowerCase();
                      const optionLabel = option.label.toLowerCase();
                      const optionGroup = option.data?.grupo?.toLowerCase() || '';
                      return optionLabel.includes(searchValue) || optionGroup.includes(searchValue);
                    }}
                  />
                  <small className="form-help">
                    Escribe al menos 2 caracteres para buscar. Ejemplo: "medicina", "enfermería", "psicología"
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Teléfono *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="Teléfono"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Correo *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Correo"
                  />
                  {formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                    <span className="error-message">E-mail no válido</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Contraseña *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Contraseña"
                    minLength="8"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmar Contraseña *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    placeholder="Confirmar Contraseña"
                  />
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <span className="error-message">*No coinciden</span>
                  )}
                </div>

                <div className="form-group full-width">
                  <label htmlFor="company">Empresa/Clínica (Opcional)</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Nombre de tu organización"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="terms-section">
                <div className="copyright-notice">
                  © Copyright 2025 - Think Health S.A.P.I - Todos los derechos reservados
                </div>
                
                <div className="terms-checkbox">
                  <label className="">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms || false}
                      onChange={handleInputChange}
                      disabled={!formData.acceptTerms}
                      required
                    />
                    {/* <span className="checkmark"></span> */}
                    <span className="">&nbsp;&nbsp;</span>
                    Acepto 
                    <button 
                      type="button" 
                      className="link-button" 
                      onClick={() => setIsPrivacyModalOpen(true)}
                    >
                      términos y condiciones y aviso de privacidad
                    </button>
                  </label>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="glass-button secondary" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="glass-button primary">
                Continuar a Institución
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <QRScanner
        isOpen={isQRScannerOpen}
        onClose={() => setIsQRScannerOpen(false)}
        onScanResult={handleQRScanResult}
      />
      
      <PrivacyNoticeModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        onAccept={() => {
          // Auto-marcar el checkbox cuando acepten
          const syntheticEvent = {
            target: {
              name: 'acceptTerms',
              type: 'checkbox',
              checked: true
            }
          };
          handleInputChange(syntheticEvent);
          // Cerrar el modal después de un pequeño delay para asegurar que el estado se actualice
          setTimeout(() => {
            setIsPrivacyModalOpen(false);
          }, 100);
        }}
        institution=""
        showAcceptButton={true}
      />
    </div>
  );
};

export default PlanFormModal;
